/**
 * useWorkflowDesigner - Camada de dados do construtor visual do Workflow 2.0
 *
 * Todo acesso é feito por RPCs seguras (SECURITY DEFINER) já criadas nas fases
 * 1 a 5 do motor: leitura por `get_workflow_overview` / `get_workflow_editor` e
 * autoria por `upsert_workflow_definition`, `upsert_workflow_node`,
 * `upsert_workflow_edge`, `delete_workflow_node`, `delete_workflow_edge`,
 * `save_workflow_canvas`, `set_workflow_definition_status` e
 * `publish_workflow_definition`. Nenhuma consulta direta a tabelas.
 *
 * @version 1.0.0
 * RELEASE: 2026-08-30 v1.0.0 - Criação inicial (designer v2: nodes, arestas,
 *          gateways, junções, esperas/temporizadores e ações).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import { toast } from 'sonner';

const logger = createLogger('useWorkflowDesigner');

// =============================================================================
// TIPOS DO DESIGNER (espelham o contrato das RPCs)
// =============================================================================

/** Tipos de node suportados pelo motor (enum `workflow_node_type`) */
export type WorkflowNodeType =
  | 'inicio'
  | 'acao'
  | 'condicao'
  | 'gateway'
  | 'espera'
  | 'tarefa_humana'
  | 'subworkflow'
  | 'fim';

/** Ação configurada em um node (contrato lido por `workflow_enter_node`) */
export interface WorkflowActionConfig {
  action_code: string;
  params?: Record<string, unknown>;
}

export interface WorkflowEditorNode {
  id: string;
  code: string | null;
  label: string;
  node_type: WorkflowNodeType;
  config: Record<string, any> | null;
  entry_conditions: any;
  on_enter_actions: WorkflowActionConfig[] | null;
  on_complete_actions: WorkflowActionConfig[] | null;
  position_x: number | null;
  position_y: number | null;
  sort_order: number | null;
}

export interface WorkflowEditorEdge {
  id: string;
  from_node_id: string;
  to_node_id: string;
  label: string | null;
  event_code: string | null;
  conditions: any;
  enforcement: string | null;
  is_default: boolean | null;
  is_regression: boolean | null;
  sort_order: number | null;
}

export interface WorkflowCatalogItem {
  code: string;
  label: string;
  description?: string | null;
  module?: string | null;
  entity_type?: string | null;
  params_contract?: any;
}

export interface WorkflowEditorPayload {
  definition: {
    id: string;
    name: string;
    description: string | null;
    status: string;
    root_entity_type: string;
    default_enforcement: string;
    active_version_id: string | null;
    workspace_ids: string[];
  };
  draft_version_id: string;
  nodes: WorkflowEditorNode[];
  edges: WorkflowEditorEdge[];
  versions: Array<{ id: string; version_number: number; status: string; published_at: string | null }>;
  event_catalog: WorkflowCatalogItem[];
  action_catalog: WorkflowCatalogItem[];
  /** Outros processos ativos da mesma entidade-raiz que já usam os mesmos gatilhos */
  trigger_conflicts?: Array<{ definition_id: string; name: string; event_codes: string[] }>;
  validation: any;
}


export interface WorkflowOverviewPayload {
  workflow_enabled: boolean;
  definitions: Array<{
    id: string;
    name: string;
    description: string | null;
    status: string;
    /** Data em que o processo foi desativado (null quando ativo/rascunho) */
    archived_at?: string | null;
    root_entity_type: string;
    root_entity_label: string | null;
    default_enforcement: string;
    active_version_id: string | null;
    active_version_number: number | null;
    updated_at: string;
    workspace_ids: string[];
    workspace_names: string[];
    node_count: number;
    edge_count: number;
    /** Total de execuções (histórico completo) */
    instance_count?: number;
    /** Execuções em andamento — bloqueiam a exclusão do processo */
    open_instance_count?: number;
  }>;
  entity_types: Array<{ code: string; label: string; description: string | null; icon: string | null; is_active: boolean }>;
}

/** Executa uma RPC do módulo já injetando o tenant como dica (validada no banco) */
async function callRpc<T>(fn: string, params: Record<string, unknown>, tenantId?: string | null): Promise<T> {
  const { data, error } = await (supabase.rpc as any)(fn, { ...params, p_tenant_hint: tenantId ?? null });
  if (error) {
    logger.error(`Falha na RPC ${fn}`, { error: error.message });
    throw error;
  }
  return data as T;
}

// =============================================================================
// LISTAGEM DE PROCESSOS
// =============================================================================

/** Visão geral dos processos do tenant (usada na página de listagem) */
export function useWorkflowOverview() {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['workflow-overview', tenantId],
    enabled: !!tenantId,
    staleTime: 30_000,
    queryFn: () => callRpc<WorkflowOverviewPayload>('get_workflow_overview', {}, tenantId),
  });

  /** Cria ou atualiza a definição do processo */
  const upsertDefinition = useMutation({
    mutationFn: (input: {
      id?: string | null;
      name: string;
      description?: string | null;
      root_entity_type: string;
      workspace_ids?: string[] | null;
      default_enforcement?: string | null;
    }) =>
      callRpc<string>(
        'upsert_workflow_definition',
        {
          p_id: input.id ?? null,
          p_name: input.name,
          p_description: input.description ?? null,
          p_root_entity_type: input.root_entity_type,
          p_workspace_ids: input.workspace_ids ?? null,
          p_default_enforcement: input.default_enforcement ?? null,
        },
        tenantId,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflow-overview', tenantId] });
      // Também invalida o cache do construtor, senão os campos editados
      // (nome, descrição, workspaces, bloqueio) só apareceriam após F5.
      queryClient.invalidateQueries({ queryKey: ['workflow-editor'] });
      toast.success('Processo salvo');
    },

    onError: (e: any) => toast.error(e?.message || 'Não foi possível salvar o processo'),
  });

  /** Altera o status da definição (draft / active / archived) */
  const setStatus = useMutation({
    mutationFn: (input: { id: string; status: string }) =>
      callRpc<void>('set_workflow_definition_status', { p_definition_id: input.id, p_status: input.status }, tenantId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflow-overview', tenantId] });
      // Status também é exibido no cabeçalho do construtor.
      queryClient.invalidateQueries({ queryKey: ['workflow-editor'] });
      toast.success('Status atualizado');
    },

    onError: (e: any) => toast.error(e?.message || 'Não foi possível alterar o status'),
  });

  /**
   * Exclui definitivamente o processo.
   * O banco bloqueia a exclusão quando existem execuções em andamento e exige
   * `confirmPurge` quando há histórico encerrado a ser apagado em cascata.
   */
  const deleteDefinition = useMutation({
    mutationFn: (input: { id: string; confirmPurge?: boolean }) =>
      callRpc<void>(
        'delete_workflow_definition',
        { p_definition_id: input.id, p_confirm_purge: input.confirmPurge ?? false },
        tenantId,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflow-overview', tenantId] });
      toast.success('Processo excluído');
    },
    onError: (e: any) => toast.error(e?.message || 'Não foi possível excluir o processo'),
  });

  return {
    overview: query.data,
    isLoading: query.isLoading,
    refetch: query.refetch,
    upsertDefinition,
    setStatus,
    deleteDefinition,
  };
}

// =============================================================================
// EDITOR (GRAFO)
// =============================================================================

/** Dados e mutações do construtor visual de um processo */
export function useWorkflowEditor(definitionId?: string) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;
  const queryClient = useQueryClient();
  const key = ['workflow-editor', tenantId, definitionId];

  const query = useQuery({
    queryKey: key,
    enabled: !!tenantId && !!definitionId,
    staleTime: 15_000,
    queryFn: () => callRpc<WorkflowEditorPayload>('get_workflow_editor', { p_definition_id: definitionId }, tenantId),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: key });

  /** Cria/atualiza um node do rascunho (paleta e inspetor usam esta mutação) */
  const upsertNode = useMutation({
    mutationFn: (input: {
      id?: string | null;
      label: string;
      node_type: WorkflowNodeType;
      code?: string | null;
      config?: Record<string, unknown> | null;
      entry_conditions?: unknown;
      on_enter_actions?: WorkflowActionConfig[] | null;
      on_complete_actions?: WorkflowActionConfig[] | null;
      position_x?: number | null;
      position_y?: number | null;
      sort_order?: number | null;
    }) =>
      callRpc<string>(
        'upsert_workflow_node',
        {
          p_definition_id: definitionId,
          p_id: input.id ?? null,
          p_label: input.label,
          p_node_type: input.node_type,
          p_code: input.code ?? null,
          p_config: input.config ?? {},
          p_entry_conditions: input.entry_conditions ?? null,
          p_on_enter_actions: input.on_enter_actions ?? null,
          p_on_complete_actions: input.on_complete_actions ?? null,
          p_position_x: input.position_x ?? null,
          p_position_y: input.position_y ?? null,
          p_sort_order: input.sort_order ?? null,
        },
        tenantId,
      ),
    onSuccess: () => invalidate(),
    onError: (e: any) => toast.error(e?.message || 'Não foi possível salvar a etapa'),
  });

  /** Cria/atualiza uma ligação entre nodes */
  const upsertEdge = useMutation({
    mutationFn: (input: {
      id?: string | null;
      from_node_id: string;
      to_node_id: string;
      label?: string | null;
      event_code?: string | null;
      conditions?: unknown;
      enforcement?: string | null;
      is_default?: boolean | null;
      is_regression?: boolean | null;
      sort_order?: number | null;
    }) =>
      callRpc<string>(
        'upsert_workflow_edge',
        {
          p_definition_id: definitionId,
          p_id: input.id ?? null,
          p_from_node_id: input.from_node_id,
          p_to_node_id: input.to_node_id,
          p_label: input.label ?? null,
          p_event_code: input.event_code ?? null,
          p_conditions: input.conditions ?? null,
          // Padrões explícitos: colunas NOT NULL no banco não aceitam nulo
          p_enforcement: input.enforcement ?? 'allow',
          p_is_default: input.is_default ?? false,
          p_is_regression: input.is_regression ?? false,
          p_sort_order: input.sort_order ?? null,
        },
        tenantId,
      ),
    onSuccess: () => invalidate(),
    onError: (e: any) => toast.error(e?.message || 'Não foi possível salvar a ligação'),
  });

  const deleteNode = useMutation({
    mutationFn: (id: string) => callRpc<void>('delete_workflow_node', { p_node_id: id }, tenantId),
    onSuccess: () => invalidate(),
    onError: (e: any) => toast.error(e?.message || 'Não foi possível excluir a etapa'),
  });

  const deleteEdge = useMutation({
    mutationFn: (id: string) => callRpc<void>('delete_workflow_edge', { p_edge_id: id }, tenantId),
    onSuccess: () => invalidate(),
    onError: (e: any) => toast.error(e?.message || 'Não foi possível excluir a ligação'),
  });

  /**
   * Persiste apenas as posições do canvas (arrastar e soltar / Organizar).
   * v3.4.0: passa a avisar o usuário quando a gravação falha (ex.: versão
   * publicada é imutável) — antes o erro só ia para o log e a tela parecia
   * "não fazer nada" ao clicar em Organizar.
   */
  const saveCanvas = useMutation({
    mutationFn: (positions: Array<{ id: string; x: number; y: number }>) =>
      callRpc<void>('save_workflow_canvas', { p_definition_id: definitionId, p_positions: positions }, tenantId),
    onError: (e: any) => {
      logger.warn('Falha ao salvar posições do canvas', { error: e?.message });
      toast.error(e?.message || 'Não foi possível salvar a organização do mapa');
    },
  });


  /**
   * Publica o rascunho como nova versão imutável.
   * A política retroativa apenas autoriza avaliação sob demanda ao abrir o
   * registro; nunca inicia varredura ou processamento em lote na publicação.
   */
  const publish = useMutation({
    mutationFn: (input: { notes?: string | null; retroactiveOnAccess: boolean }) =>
      callRpc<any>(
        'publish_workflow_definition',
        {
          p_definition_id: definitionId,
          p_notes: input.notes ?? null,
          p_retroactive_on_access: input.retroactiveOnAccess,
        },
        tenantId,
      ),
    onSuccess: () => {
      invalidate();
      queryClient.invalidateQueries({ queryKey: ['workflow-overview', tenantId] });
      toast.success('Processo publicado');
    },
    onError: (e: any) => toast.error(e?.message || 'Não foi possível publicar o processo'),
  });

  return {
    editor: query.data,
    isLoading: query.isLoading,
    refetch: query.refetch,
    upsertNode,
    upsertEdge,
    deleteNode,
    deleteEdge,
    saveCanvas,
    publish,
  };
}

// =============================================================================
// FASE 8: SIMULADOR (DRY-RUN) E OBSERVABILIDADE
// =============================================================================

/** Passo retornado pelo simulador (`simulate_workflow_definition`) */
export interface WorkflowSimulationStep {
  ordem: number;
  node_id: string;
  label: string;
  node_type: WorkflowNodeType;
  acoes: WorkflowActionConfig[];
  observacao: string | null;
}

export interface WorkflowSimulationPayload {
  version_id: string;
  passos: WorkflowSimulationStep[];
  total_passos: number;
  limite_atingido: boolean;
}

/**
 * Simulação em modo seco do processo: percorre o desenho sem gravar nada e sem
 * executar nenhuma ação (a RPC é somente leitura do grafo).
 */
export function useWorkflowSimulation(definitionId?: string) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;

  return useMutation({
    mutationFn: (context: Record<string, unknown>) =>
      callRpc<WorkflowSimulationPayload>(
        'simulate_workflow_definition',
        { p_definition_id: definitionId, p_context: context ?? {}, p_max_steps: 60 },
        tenantId,
      ),
    onError: (e: any) => toast.error(e?.message || 'Não foi possível simular o processo'),
  });
}

/** Gargalos: tempo médio por bloco e processos parados (`get_workflow_bottlenecks`) */
export interface WorkflowBottlenecksPayload {
  nodes: Array<{
    node_id: string;
    label: string;
    node_type: WorkflowNodeType;
    /** Processo (workflow) ao qual a etapa pertence — v1.1.0 da RPC */
    definition_id: string;
    definition_name: string;
    execucoes: number;
    minutos_medio: number | null;
    em_aberto: number;
  }>;
  instancias_paradas: number;
  dias: number;
}

export function useWorkflowBottlenecks(definitionId?: string | null, dias = 30) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;

  return useQuery({
    queryKey: ['workflow-bottlenecks', tenantId, definitionId ?? 'todos', dias],
    enabled: !!tenantId,
    staleTime: 60_000,
    queryFn: () =>
      callRpc<WorkflowBottlenecksPayload>(
        'get_workflow_bottlenecks',
        { p_definition_id: definitionId ?? null, p_days: dias },
        tenantId,
      ),
  });
}
